import { ApiClient } from './apiClient'
import type {
  Organization,
  OrganizationCreationPayload,
  OrganizationUpdatePayload,
} from '@/types/organization'
import type { Team } from '@/types/team'
import type { User } from '@/types/user'

class OrganizationService extends ApiClient {
  async create(organization: Partial<OrganizationCreationPayload>): Promise<Organization> {
    const response = await this.post<Organization>('/organizations/', organization, { version: 'v2' })
    return response.data
  }

  // tpa_admin-only edit of an org's own fields (name/nif/language) + its full
  // domain set. Backend: PUT /v2/organizations/:id (re-checks IsTPAAdmin).
  async update(organizationId: string, payload: OrganizationUpdatePayload): Promise<Organization> {
    const response = await this.put<Organization>(`/organizations/${organizationId}`, payload, { version: 'v2' })
    return response.data
  }

  // Registered email domains for an org. Returns the domain strings (the edit
  // modal prefills its dynamic list from these).
  async fetchDomains(organizationId: string): Promise<string[]> {
    const response = await this.get<Array<{ domain: string }>>(
      `/organizations/${organizationId}/domains`,
      { version: 'v2' },
    )
    return (response.data ?? []).map((d) => d.domain)
  }

  async activate(organizationId: string): Promise<void> {
    await this.post(`/organizations/${organizationId}/activate`, undefined, { version: 'v2' })
  }

  async deactivate(organizationId: string): Promise<void> {
    await this.post(`/organizations/${organizationId}/deactivate`, undefined, { version: 'v2' })
  }

  async fetchAll(): Promise<Organization[]> {
    const response = await this.get<Organization[]>('/organizations', { version: 'v2' })
    return response.data
  }

  // The caller's own organizations - admin or not, entity-sourced (includes
  // cross-org membership granted via domain-based team joins). Unlike
  // fetchAll(), which returns every organization in the system for a
  // tpa_admin, this always answers "which orgs do I actually belong to".
  // Used by the sidebar to decide whether teams should be grouped under
  // organization headers.
  async fetchMine(): Promise<Organization[]> {
    const response = await this.get<Organization[]>('/organizations/mine', { version: 'v2' })
    return response.data ?? []
  }

  async fetchById(organizationId: string): Promise<Organization> {
    const response = await this.get<Organization>(`/organizations/${organizationId}`, { version: 'v2' })
    return response.data
  }

  // Organization roster, sourced from user_entities (org_leader direct links
  // plus the session -> team -> organization chain). Returns plain users;
  // per-member org roles come from authService.getRBACRolesBatch with
  // entity 'organizations' (see OrganizationUsersList / OrganizationView).
  async fetchUsers(organizationId: string): Promise<User[]> {
    const response = await this.get<User[]>(`/organizations/${organizationId}/users`, { version: 'v2' })
    return response.data
  }

  async fetchCurrentUserOrganization(): Promise<Organization> {
    const response = await this.get<Organization>('/users/me/organization')
    return response.data
  }

  // Single org member, sourced from user_entities. Returns the plain user
  // (404 when they don't belong to the org).
  async fetchUser(organizationId: string, userId: string): Promise<User> {
    const response = await this.get<User>(
      `/organizations/${organizationId}/users/${userId}`,
      { version: 'v2' },
    )
    return response.data
  }

  // Org teams scoped to what the caller may see. v2 - the v1 organizations
  // group is not mounted (org routes consolidated on v2). Derives from
  // teams.organization_id (organizations.team_ids dropped in migration 0007).
  async fetchTeams(organizationId: string): Promise<Team[]> {
    const response = await this.get<Team[]>(`/organizations/${organizationId}/teams`, {
      version: 'v2',
    })
    return response.data
  }

  async replaceOrgLeader(organizationId: string, email: string): Promise<void> {
    await this.put(`/organizations/${organizationId}/replace-org-leader`, { email }, { version: 'v2' })
  }

  async addOrgLeader(organizationId: string, email: string): Promise<void> {
    await this.post(`/organizations/${organizationId}/leader`, { email }, { version: 'v2' })
  }

  // Scope-aware "remove from this org". Privileged users (tpa_admin,
  // org_leader, team_leader) keep their User row + every artifact
  // outside this org; regular members are fully deleted server-side.
  // Never use userService.deleteUser() for org member removal - that
  // hits the global hard-delete path with no privilege guard.
  async removeUser(organizationId: string, userId: string): Promise<void> {
    await this.delete(`/organizations/${organizationId}/users/${userId}`, { version: 'v2' })
  }
}

export const organizationService = new OrganizationService()
